import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';

import { RapiderListGridComponent } from '@rapider/list-grid';
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { FormsModule } from '@angular/forms';
import { Subject, throwError, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, catchError } from 'rxjs/operators';

import { ProjectModelControllerService } from '@rapider/sdk';
import {
  ProjectDataService,
  selectProjectModelsList,
  findProjectModelByName,
} from '@rapider/project-data';
import { AuthStore } from '@rapider/authentication';
import {
  DynamicFormComponent,
  DynamicFormSchema,
  DynamicFormField,
} from '@rapider/dynamic-form';

import {
  DATA_INSIGHT_CONFIG,
  extractUiEntityConfig,
  buildFetchRecordsWithRelationsPath,
  generateTimelineGridConfig,
  getRelationDisplayType,
  buildUpdateDiffClassMap,
  getGridConfigForModel,
  getTargetModelTitle,
} from '../database.config';
import { DataCardComponent } from '../data-card/data-card.component';

/**
 * @class ProjectModelDataViewerComponent
 * @description A generic, reusable component that fetches and visualizes project model records alongside their relation data.
 * Leverages the `uiEntityConfig` metadata automatically to apply proper data grid columns and API filters.
 */
@Component({
  selector: 'rapider-project-model-data-viewer',
  standalone: true,
  imports: [
    CommonModule,
    RapiderListGridComponent,
    RapiderJsonViewerComponent,
    NzTagModule,
    NzDividerModule,
    DataCardComponent,
    NzPaginationModule,
    NzModalModule,
    NzButtonModule,
    NzTooltipModule,
    NzInputModule,
    NzIconModule,
    FormsModule,
    DynamicFormComponent,
  ],
  templateUrl: './project-model-data-viewer.component.html',
})
export class ProjectModelDataViewerComponent implements OnInit, OnChanges {
  private readonly store = inject(Store);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly projectModelController = inject(
    ProjectModelControllerService,
  );
  private readonly authStore = inject(AuthStore);
  private readonly message = inject(NzMessageService);
  private readonly modalService = inject(NzModalService);

  @Input({ required: true }) projectModelName!: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() explicitModel?: any;
  @Input() recordIds?: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() payloadData: any = null;
  @Input() operationType: string = 'READ';
  @Input() refreshTrigger: number = 0;

  // Fullscreen state and toggle emitter for parent coordination
  @Input() isFullscreen = false;
  @Output() toggleFullscreen = new EventEmitter<void>();

  public config = DATA_INSIGHT_CONFIG;
  public getRelationDisplayType = getRelationDisplayType;
  public generateTimelineGridConfig = generateTimelineGridConfig;
  public getGridConfigForModel = getGridConfigForModel;
  public getTargetModelTitle = getTargetModelTitle;

  public isLoading = signal<boolean>(false);
  public isInitialLoad = signal<boolean>(true);
  public isRelationsLoading = signal<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public data = signal<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectedRecord = signal<any | null>(null);
  public hoveredRecord = signal<any | null>(null);

  // Form State
  public isFormModalVisible = signal<boolean>(false);
  public activeFormSchema = signal<DynamicFormSchema | null>(null);
  public activeFormData = signal<any>({});
  public activeFormOriginalData = signal<any>({});
  public isEditing = signal<boolean>(false);

  // Dynamically returns the selected record, or falls back to the hovered preview if nothing is explicitly selected
  public activeDisplayRecord = computed(
    () => this.selectedRecord() || this.hoveredRecord(),
  );

  public cachedModels = computed(
    () => this.store.selectSignal(selectProjectModelsList)() || [],
  );

  // Pagination and Search State Signals
  public searchQuery = signal<string>('');
  public currentPage = signal<number>(1);
  public pageSize = computed<number>(() => {
    // Dynamically retrieve pagination limit from the listGridConfig, defaulting to 40
    const configSize = this.mainGridConfig()?.pagination?.pageSize;
    return configSize && typeof configSize === 'number' ? configSize : 40;
  });
  public totalItems = signal<number>(0);

  // Local text config to strictly avoid hardcoded text
  public localTexts = {
    specificRecordsInfo: $localize`:@@dataViewer.specificRecordsInfo:Showing results for specific records`,
    noData: $localize`:@@dataViewer.noDataSpreadsheet:No data available. Please enter data directly into the spreadsheet to create new records.`,
  };

  /**
   * PURE FUNCTION: Constructs the API path to fetch paginated "All Data".
   * @param {string} modelName - The active datatable name.
   * @param {number} skip - Number of records to bypass.
   * @param {number} limit - The page size limit.
   * @returns {string} Restful GET path with query variables.
   */
  private buildFindAllPath(
    modelName: string,
    skip: number,
    limit: number,
    searchQuery?: string,
  ): string {
    const useProjectApi = this.authStore.useProjectApiOnDataManagerCalls();
    const dataTableName = this.model()?.dataTableName || modelName;
    const basePath = useProjectApi
      ? this.model()?.basePath || `/${modelName.toLowerCase()}`
      : `/project-data-manager/${dataTableName}`;

    const filter: any = { skip, limit };
    if (searchQuery) {
      const stringColumns = (this.mainGridConfig()?.columns || [])
        .filter(
          (c: any) =>
            (c.type === 'string' ||
              c.type === 'text' ||
              c.type === 'link' ||
              !c.type) &&
            c.fieldName !== 'id',
        )
        .map((c: any) => c.fieldName);

      if (stringColumns.length > 0) {
        if (useProjectApi) {
          filter.where = {
            or: stringColumns.map((col: string) => ({
              [col]: { like: `%${searchQuery}%`, options: 'i' },
            })),
          };
        } else {
          filter.where = {
            $or: stringColumns.map((col: string) => ({
              [col]: { $regex: searchQuery, $options: 'i' },
            })),
          };
        }
      }
    }
    return `${basePath}?filter=${encodeURIComponent(JSON.stringify(filter))}`;
  }

  private buildCountPath(modelName: string, searchQuery?: string): string {
    const useProjectApi = this.authStore.useProjectApiOnDataManagerCalls();
    const dataTableName = this.model()?.dataTableName || modelName;
    const basePath = useProjectApi
      ? this.model()?.basePath || `/${modelName.toLowerCase()}`
      : `/project-data-manager/${dataTableName}`;

    if (searchQuery) {
      const stringColumns = (this.mainGridConfig()?.columns || [])
        .filter(
          (c: any) =>
            (c.type === 'string' ||
              c.type === 'text' ||
              c.type === 'link' ||
              !c.type) &&
            c.fieldName !== 'id',
        )
        .map((c: any) => c.fieldName);

      if (stringColumns.length > 0) {
        const whereClause = useProjectApi
          ? {
              or: stringColumns.map((col: string) => ({
                [col]: { like: `%${searchQuery}%`, options: 'i' },
              })),
            }
          : {
              $or: stringColumns.map((col: string) => ({
                [col]: { $regex: searchQuery, $options: 'i' },
              })),
            };
        return `${basePath}/count?where=${encodeURIComponent(JSON.stringify(whereClause))}`;
      }
    }

    return `${basePath}/count`;
  }

  public model = computed(() => {
    if (this.explicitModel) return this.explicitModel;
    const models = this.cachedModels();
    if (!models) return null;
    return findProjectModelByName(models, this.projectModelName);
  });

  public uiConfig = computed(() => extractUiEntityConfig(this.model()));

  public mainGridConfig = computed(() => {
    let baseConfig = this.uiConfig()?.config?.listGridConfig;

    const hiddenFields = [
      'createddate',
      'createdby',
      'createdbyid',
      'updateddate',
      'updatedby',
      'updatedbyid',
      'deleteddate',
      'deletedby',
      'deletedbyid',
      'id',
      '_id',
    ];

    if (!baseConfig) {
      let columns = [];
      let modelFields = this.model()?.fields;

      // Explicitly add 'id' to the fields list if it doesn't exist, since LoopBack creates it implicitly
      if (
        modelFields &&
        Array.isArray(modelFields) &&
        !modelFields.find((f: any) => f.name === 'id')
      ) {
        modelFields = [
          { name: 'id', title: 'ID', type: 'string' },
          ...modelFields,
        ];
      }

      if (modelFields && Array.isArray(modelFields) && modelFields.length > 0) {
        columns = modelFields
          .filter((f: any) => f.name !== 'isDeleted')
          .map((f: any) => ({
            fieldName: f.name,
            title: f.title || f.name,
            type: f.type === 'number' ? 'number' : 'text',
            visible: !hiddenFields.includes(f.name.toLowerCase()),
          }));
        baseConfig = {
          columns,
          searchable: true,
          filterable: false,
          sortable: true,
          showPagination: false,
        };
      } else {
        baseConfig = generateTimelineGridConfig(this.data());
        if (baseConfig.columns) {
          baseConfig.columns = baseConfig.columns
            .filter((col: any) => col.fieldName !== 'isDeleted')
            .map((col: any) => {
              if (hiddenFields.includes(col.fieldName.toLowerCase())) {
                return { ...col, visible: false };
              }
              return col;
            });
        }
      }
    } else {
      // Clone config to prevent mutating NgRx frozen state
      baseConfig = {
        ...baseConfig,
        columns: baseConfig.columns ? [...baseConfig.columns] : [],
      };

      // Explicitly inject ID column if it doesn't exist, so users can select it
      if (
        !baseConfig.columns.find(
          (c: any) => c.fieldName === 'id' || c.fieldName === '_id',
        )
      ) {
        baseConfig.columns = [
          { fieldName: 'id', title: 'ID', type: 'text', visible: false },
          ...baseConfig.columns,
        ];
      }

      if (baseConfig.columns) {
        baseConfig.columns = baseConfig.columns
          .filter((col: any) => col.fieldName !== 'isDeleted')
          .map((col: any) => {
            if (hiddenFields.includes(col.fieldName.toLowerCase())) {
              return { ...col, visible: false };
            }
            return col;
          });
      }
    }

    // Always force showPagination false since we use an external nz-pagination
    baseConfig.showPagination = false;

    // Always inject item actions and column visibility for the data viewer
    return {
      ...baseConfig,
      columnVisibilitySelectable: true,
      itemActions: [
        {
          name: 'edit',
          icon: { name: 'fas fa-pen' },
          type: 'default',
          text: 'Edit',
        },
        {
          name: 'delete',
          icon: { name: 'fas fa-trash' },
          type: 'default',
          text: 'Delete',
          danger: true,
        },
      ],
    };
  });

  public diffClassMap = computed(() => {
    if (this.operationType === 'UPDATE' && this.payloadData) {
      return buildUpdateDiffClassMap(this.payloadData);
    }
    return {};
  });

  ngOnInit() {
    this.fetchRelationsAndData();
  }

  public onGridSearchSubmit(query: string): void {
    this.searchQuery.set(query);
    this.currentPage.set(1);
    this.fetchRelationsAndData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['projectModelName'] &&
        !changes['projectModelName'].isFirstChange()) ||
      (changes['recordIds'] && !changes['recordIds'].isFirstChange()) ||
      (changes['refreshTrigger'] && !changes['refreshTrigger'].isFirstChange())
    ) {
      this.currentPage.set(1); // Reset to first page on context change
      this.searchQuery.set('');
      this.fetchRelationsAndData();
    }
  }

  private fetchRelationsAndData(): void {
    const currentModel = this.model();

    if (!currentModel || !currentModel.id) {
      // If model doesn't exist in store, we attempt to fetch it dynamically using the smart cache wrapper.
      this.isLoading.set(true);

      this.projectDataService
        .getProjectModelWithoutRelations(this.projectModelName)
        .subscribe({
          next: (res: any[]) => {
            if (res && res.length > 0) {
              this.fetchFullModelAndRelations(res[0].id);
            } else {
              this.fetchData([]); // Proceed without relations if not found
            }
          },
          error: () => this.fetchData([]),
        });
      return;
    }

    if (
      currentModel.relations &&
      Array.isArray(currentModel.relations) &&
      currentModel.fields &&
      Array.isArray(currentModel.fields)
    ) {
      // Relations and full model details are already cached in the NgRx store
      this.fetchData(currentModel.relations);
    } else {
      // Relations are missing, fetch the full model and its relations via the centralized cache wrapper
      this.fetchFullModelAndRelations(currentModel.id);
    }
  }

  /**
   * Fetches the comprehensive model details via getProjectModelCompleteDetail.
   * Caches the enriched model back into the NgRx store automatically.
   * @param {string} modelId - The ID of the project model.
   */
  private fetchFullModelAndRelations(modelId: string): void {
    this.isRelationsLoading.set(true);

    this.projectDataService.getProjectModelCompleteDetail(modelId).subscribe({
      next: (completeModel) => {
        this.isRelationsLoading.set(false);

        // Proceed to fetch the main record data now that we know what to include
        this.fetchData(completeModel.relations || []);
      },
      error: (err) => {
        console.error('Failed to fetch model and relations:', err);
        this.isRelationsLoading.set(false);
        this.fetchData([]); // Proceed without relations if the fetch fails
      },
    });
  }

  private fetchData(relations: any[]): void {
    if (!this.projectModelName) {
      this.data.set([]);
      this.selectedRecord.set(null);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    const isSpecificRecordsMode = this.recordIds && this.recordIds.length > 0;

    if (isSpecificRecordsMode) {
      // MODE 1: Specific Records (Unpaginated Sequence)
      const dataTableName =
        this.model()?.dataTableName || this.projectModelName;
      const useProjectApi = this.authStore.useProjectApiOnDataManagerCalls();
      const path = buildFetchRecordsWithRelationsPath(
        this.projectModelName,
        dataTableName,
        this.recordIds!,
        relations,
        useProjectApi,
      );

      this.projectDataService
        .executeProjectApiCall('GET', path, null)
        .subscribe({
          next: (response) => {
            const arr = Array.isArray(response)
              ? response
              : response
                ? [response]
                : [];

            // SORT to strictly match the incoming recordIds sequence (Newest -> Oldest)
            const sortedArr: any[] = [];
            const idMap = new Map();
            arr.forEach((item) => {
              const id = item.id || item._id;
              if (id) idMap.set(id.toString(), item);
            });

            // Push matching records in the exact priority order
            this.recordIds!.forEach((id) => {
              const key = id.toString();
              if (idMap.has(key)) {
                sortedArr.push(idMap.get(key));
                idMap.delete(key);
              }
            });

            // Append any stragglers safely (fallback)
            idMap.forEach((item) => sortedArr.push(item));

            this.data.set(sortedArr);
            this.totalItems.set(sortedArr.length);

            // Automatically select the very first record (the newest/active one)
            if (sortedArr.length > 0) {
              this.selectedRecord.set(sortedArr[0]);
            } else {
              this.selectedRecord.set(null);
            }

            this.isLoading.set(false);
            this.isInitialLoad.set(false);
          },
          error: () => {
            this.data.set([]);
            this.totalItems.set(0);
            this.selectedRecord.set(null);
            this.isLoading.set(false);
            this.isInitialLoad.set(false);
          },
        });
    } else {
      // MODE 2: "All Data" with Pagination via Find API
      const limit = this.pageSize();
      const skip = (this.currentPage() - 1) * limit;
      const path = this.buildFindAllPath(
        this.projectModelName,
        skip,
        limit,
        this.searchQuery(),
      );

      const dataTableName = this.model()?.dataTableName || this.projectModelName;

      this.projectDataService
        .executeProjectApiCall('GET', path, null)
        .pipe(
          catchError((err) => {
            // Resilient fallback: If project API failed (e.g. 404 or container down) and useProjectApi was enabled,
            // fall back to /project-data-manager/${dataTableName} directly
            if (this.authStore.useProjectApiOnDataManagerCalls()) {
              const fallbackPath = `/project-data-manager/${dataTableName}?filter=${encodeURIComponent(
                JSON.stringify({ skip, limit }),
              )}`;
              return this.projectDataService.executeProjectApiCall('GET', fallbackPath, null);
            }
            return throwError(() => err);
          }),
        )
        .subscribe({
          next: (response: any) => {
            // Robust mapping to catch standard arrays or structured paginated responses { items, total }
            let records: any[] = [];
            let totalCount = 0;

            if (Array.isArray(response)) {
              records = response;
              // If records returned equals the limit, there are more pages available
              const minimumExpected =
                records.length === limit
                  ? this.currentPage() * limit + 1
                  : (this.currentPage() - 1) * limit + records.length;
              totalCount =
                this.totalItems() >= minimumExpected
                  ? this.totalItems()
                  : minimumExpected;
            } else if (response && response.items) {
              records = response.items;
              totalCount = response.total || response.count || records.length;
            } else if (response) {
              records = [response];
              totalCount = 1;
            }

            this.data.set(records);
            if (totalCount > 0) {
              this.totalItems.set(totalCount);
            }

            if (records.length > 0) {
              this.selectedRecord.set(records[0]);
            } else {
              this.selectedRecord.set(null);
            }

            this.isLoading.set(false);
            this.isInitialLoad.set(false);
          },
          error: () => {
            this.data.set([]);
            this.totalItems.set(0);
            this.selectedRecord.set(null);
            this.isLoading.set(false);
            this.isInitialLoad.set(false);
          },
        });

      const countPath = this.buildCountPath(
        this.projectModelName,
        this.searchQuery(),
      );
      this.projectDataService
        .executeProjectApiCall('GET', countPath, null)
        .pipe(
          catchError(() => {
            // Fallback to /project-data-manager/${dataTableName}/count if countPath failed
            const fallbackCountPath = `/project-data-manager/${dataTableName}/count`;
            return this.projectDataService.executeProjectApiCall('GET', fallbackCountPath, null);
          }),
        )
        .subscribe({
          next: (response: any) => {
            let total = 0;
            if (typeof response === 'number') {
              total = response;
            } else if (response && typeof response.count === 'number') {
              total = response.count;
            } else if (response && typeof response.total === 'number') {
              total = response.total;
            }
            if (total > 0 || !this.searchQuery()) {
              this.totalItems.set(total);
            }
          },
          error: () => {
            // Retain count if count endpoint fails
          },
        });
    }
  }

  /**
   * Action triggered when the pagination controls are interacted with.
   * @param {number} page - The selected page number.
   */
  public onPageChange(pageIndex: number): void {
    this.currentPage.set(pageIndex);
    this.fetchRelationsAndData();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onGridAction(event: any): void {
    // Safely extract the action name from the event payload
    const actionName = event?.action?.name || event?.name;

    if (actionName === 'select-item') {
      // Map the payload data into the selectedRecord signal to trigger the master-detail UI
      const recordData = event?.data || event?.item || event;
      this.selectedRecord.set(recordData);
    }
  }

  /**
   * Handles row selection on a single click. Toggles off if the same row is clicked again.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onRowClick(event: any): void {
    console.log('Row clicked:', event);
    const recordData = event?.data || event?.item || event;
    if (this.selectedRecord() === recordData) {
      this.selectedRecord.set(null); // Deselect if clicked again
    } else {
      this.selectedRecord.set(recordData);
    }
  }

  /**
   * Handles row hover to temporarily preview relations if no record is currently hard-selected.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onRowMouseEnter(event: any): void {
    const recordData = event?.data || event?.item || event;
    this.hoveredRecord.set(recordData);
  }

  /**
   * Clears the hover preview.
   */
  public onRowMouseLeave(): void {
    this.hoveredRecord.set(null);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public normalizeDataAsArray(data: any): any[] {
    if (!data) return [];
    return Array.isArray(data) ? data : [data];
  }

  // --- Dynamic Form (Add/Edit) Logic ---

  public buildFormSchema(): DynamicFormSchema | null {
    const currentModel = this.model();
    if (!currentModel) return null;
    if (!currentModel.fields || !Array.isArray(currentModel.fields)) {
      // Fallback empty schema if no fields exist yet
      return { fields: [] };
    }

    const excludedFields = [
      'id',
      '_id',
      'createdDate',
      'updatedDate',
      'deletedDate',
      'createdBy',
      'updatedBy',
      'deletedBy',
      'isDeleted',
      'createdById',
      'updatedById',
      'deletedById',
      'createdAt',
      'updatedAt',
      'deletedAt',
    ];

    const formFields: DynamicFormField[] = currentModel.fields
      .filter(
        (f: any) => !f.isId && !f.isHidden && !excludedFields.includes(f.name),
      )
      .map((f: any) => {
        const field: DynamicFormField = {
          name: f.name,
          type: f.isArray
            ? 'array'
            : f.type === 'number' || f.type === 'boolean' || f.type === 'object'
              ? f.type
              : 'string',
          label: f.title || f.name,
          required: f.isRequired,
          defaultValue: f.default,
        };

        // If it's a known relation, map it to a Select component
        if (currentModel.relations && Array.isArray(currentModel.relations)) {
          const rel = currentModel.relations.find(
            (r: any) => r.keyFromId === f.id || r.keyFrom?.name === f.name,
          );
          if (
            rel &&
            (rel.type === 'belongsTo' || rel.type === 'referencesMany')
          ) {
            field.displayWithComponent = 'Select';
            field.multiple = rel.type === 'referencesMany';
            field.options = []; // For now, empty options, can be populated lazily
          }
        }

        return field;
      });

    return { fields: formFields };
  }

  public onAddRecord(): void {
    const schema = this.buildFormSchema();
    if (schema) {
      this.activeFormSchema.set(schema);
      this.activeFormData.set({});
      this.isEditing.set(false);
      this.isFormModalVisible.set(true);
    }
  }

  public onEditRecord(record: any) {
    const schema = this.buildFormSchema();
    if (schema) {
      this.activeFormSchema.set(schema);
      this.activeFormOriginalData.set(record);
      this.activeFormData.set({ ...record });
      this.isEditing.set(true);
      this.isFormModalVisible.set(true);
    }
  }

  public onItemActionClick(event: any) {
    if (event.action?.name === 'edit') {
      this.onEditRecord(event.data);
    } else if (event.action?.name === 'delete') {
      this.onDeleteRecord(event.data);
    }
  }

  public onFormModalCancel(): void {
    this.isFormModalVisible.set(false);
  }

  public onFormChange(value: any): void {
    // Merge existing data with new values to preserve fields not in the form schema (like id)
    this.activeFormData.set({ ...this.activeFormData(), ...value });
  }

  public onFormModalOk(): void {
    const data = this.activeFormData();
    const original = this.activeFormOriginalData();
    const model = this.model();
    if (!model) return;

    const useProjectApi = this.authStore.useProjectApiOnDataManagerCalls();
    const dataTableName = model.dataTableName || model.name;
    const basePath = useProjectApi
      ? model.basePath || `/${model.name.toLowerCase()}`
      : `/project-data-manager/${dataTableName}`;

    this.isLoading.set(true);

    const restrictedFields = [
      'id',
      '_id',
      'isdeleted',
      'createddate',
      'createdby',
      'createdbyid',
      'updateddate',
      'updatedby',
      'updatedbyid',
      'deleteddate',
      'deletedby',
      'deletedbyid',
    ];

    const recordId = original?.id || original?._id;

    if (this.isEditing() && recordId) {
      // Update - Calculate delta and strip restricted fields
      const delta: any = {};
      Object.keys(data).forEach((key) => {
        if (restrictedFields.includes(key.toLowerCase())) return;

        let val1 = data[key];
        let val2 = original[key];

        if (val1 === '' || val1 === null) val1 = undefined;
        if (val2 === '' || val2 === null) val2 = undefined;

        if (JSON.stringify(val1) !== JSON.stringify(val2)) {
          delta[key] = data[key]; // Preserve the exact null/string if it actually changed
        }
      });

      this.projectDataService
        .updateProjectModelDataById(basePath, recordId, delta)
        .subscribe({
          next: () => {
            this.message.success('Record updated successfully');
            this.fetchRelationsAndData();
            this.isFormModalVisible.set(false);
          },
          error: (err) => {
            this.message.error('Failed to update record');
            console.error(err);
            this.isLoading.set(false);
          },
        });
    } else {
      // Create - Strip restricted fields and empty fields
      const cleanData: any = {};
      Object.keys(data).forEach((key) => {
        if (restrictedFields.includes(key.toLowerCase())) return;
        const val = data[key];
        if (val !== undefined && val !== null && val !== '') {
          cleanData[key] = val;
        }
      });

      this.projectDataService
        .createProjectModelData(basePath, cleanData)
        .subscribe({
          next: () => {
            this.message.success('Record created successfully');
            this.fetchRelationsAndData();
            this.isFormModalVisible.set(false);
          },
          error: (err) => {
            this.message.error('Failed to create record');
            console.error(err);
            this.isLoading.set(false);
          },
        });
    }
  }

  public onDeleteRecord(record: any): void {
    this.modalService.confirm({
      nzTitle: 'Are you sure you want to delete this record?',
      nzContent: 'There is no undoing this action.',
      nzOkText: 'Delete',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzCancelText: 'Cancel',
      nzOnOk: () => {
        const model = this.model();
        if (!model) return;

        const useProjectApi = this.authStore.useProjectApiOnDataManagerCalls();
        const dataTableName = model.dataTableName || model.name;
        const basePath = useProjectApi
          ? model.basePath || `/${model.name.toLowerCase()}`
          : `/project-data-manager/${dataTableName}`;

        this.isLoading.set(true);
        const recordId = record.id || record._id;
        if (!recordId) {
          this.message.error('Unable to determine record ID');
          this.isLoading.set(false);
          return;
        }

        this.projectDataService
          .deleteProjectModelDataById(basePath, recordId)
          .subscribe({
            next: () => {
              this.message.success('Record deleted successfully');
              this.selectedRecord.set(null);
              this.hoveredRecord.set(null);
              this.fetchRelationsAndData();
            },
            error: (err) => {
              this.message.error(
                'Failed to delete record. It may be restricted by existing relationships.',
              );
              console.error(err);
              this.isLoading.set(false);
            },
          });
      },
    });
  }
}
